// EPSS (Exploit Prediction Scoring System) API integration
// API: https://api.first.org/data/v1/epss
// EPSS is a data-driven effort for estimating the probability that a CVE
// will be exploited in the wild in the next 30 days.
// Edge runtime compatible - uses fetch API only

const EPSS_API_BASE = 'https://api.first.org/data/v1/epss';
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

export interface EPSSData {
  cve: string;
  epss: number; // Probability of exploitation (0-1)
  percentile: number; // Percentile ranking (0-1)
  date: string; // Date of EPSS calculation
}

interface EPSSAPIResponse {
  status: string;
  'status-code': number;
  version: string;
  total: number;
  offset: number;
  limit: number;
  data: Array<{
    cve: string;
    epss: string;
    percentile: string;
    date: string;
  }>;
}

// In-memory cache for edge runtime
const memoryCache: Map<string, { data: EPSSData; timestamp: number }> = new Map();

/**
 * Fetch EPSS score for a single CVE
 */
export async function fetchEPSS(cveId: string): Promise<EPSSData | null> {
  const normalizedCveId = cveId.toUpperCase();

  // Check memory cache first
  const cached = memoryCache.get(normalizedCveId);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION_MS) {
    return cached.data;
  }

  try {
    const response = await fetch(`${EPSS_API_BASE}?cve=${normalizedCveId}`, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`EPSS API returned ${response.status}`);
    }

    const result: EPSSAPIResponse = await response.json();

    if (result.status !== 'OK' || !result.data || result.data.length === 0) {
      return null;
    }

    const epssEntry = result.data[0];
    const epssData: EPSSData = {
      cve: epssEntry.cve,
      epss: parseFloat(epssEntry.epss),
      percentile: parseFloat(epssEntry.percentile),
      date: epssEntry.date,
    };

    // Update memory cache
    memoryCache.set(normalizedCveId, {
      data: epssData,
      timestamp: Date.now(),
    });

    return epssData;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error('Error fetching EPSS data:', errorMessage);

    // Return cached data if available, even if expired
    if (cached) {
      return cached.data;
    }
    return null;
  }
}

/**
 * Fetch EPSS scores for multiple CVEs in batch
 * The FIRST API supports batch queries by comma-separating CVE IDs
 */
export async function fetchEPSSBatch(cveIds: string[]): Promise<Map<string, EPSSData>> {
  const results = new Map<string, EPSSData>();

  if (cveIds.length === 0) return results;

  // Check cache and collect uncached IDs
  const uncachedIds: string[] = [];
  for (const cveId of cveIds) {
    const normalizedId = cveId.toUpperCase();
    const cached = memoryCache.get(normalizedId);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION_MS) {
      results.set(normalizedId, cached.data);
    } else {
      uncachedIds.push(normalizedId);
    }
  }

  if (uncachedIds.length === 0) {
    return results;
  }

  try {
    // FIRST API supports batch queries with comma-separated CVE IDs
    // Limit to 100 CVEs per request to avoid URL length issues
    const batchSize = 100;

    for (let i = 0; i < uncachedIds.length; i += batchSize) {
      const batch = uncachedIds.slice(i, i + batchSize);
      const cveParam = batch.join(',');

      const response = await fetch(`${EPSS_API_BASE}?cve=${cveParam}`, {
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        console.error(`EPSS batch API returned ${response.status}`);
        continue;
      }

      const result: EPSSAPIResponse = await response.json();

      if (result.status === 'OK' && result.data) {
        for (const entry of result.data) {
          const epssData: EPSSData = {
            cve: entry.cve,
            epss: parseFloat(entry.epss),
            percentile: parseFloat(entry.percentile),
            date: entry.date,
          };

          const normalizedId = entry.cve.toUpperCase();
          results.set(normalizedId, epssData);

          // Update cache
          memoryCache.set(normalizedId, {
            data: epssData,
            timestamp: Date.now(),
          });
        }
      }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error('Error fetching EPSS batch data:', errorMessage);
  }

  return results;
}

/**
 * Get EPSS severity level based on score
 * - High: >= 0.7 (70%+) - Highly likely to be exploited
 * - Medium: 0.3-0.7 (30-70%) - Moderate exploitation likelihood
 * - Low: < 0.3 (<30%) - Low exploitation likelihood
 */
export function getEPSSSeverity(epssScore: number): 'HIGH' | 'MEDIUM' | 'LOW' {
  if (epssScore >= 0.7) return 'HIGH';
  if (epssScore >= 0.3) return 'MEDIUM';
  return 'LOW';
}

/**
 * Get color class for EPSS score display
 */
export function getEPSSColor(epssScore: number): string {
  if (epssScore >= 0.7) return 'text-red-600 dark:text-red-400';
  if (epssScore >= 0.3) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-green-600 dark:text-green-400';
}

/**
 * Get background color class for EPSS badge
 */
export function getEPSSBgColor(epssScore: number): string {
  if (epssScore >= 0.7) return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  if (epssScore >= 0.3) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
  return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
}

/**
 * Format EPSS score as percentage string
 */
export function formatEPSSScore(epssScore: number): string {
  return `${(epssScore * 100).toFixed(2)}%`;
}

/**
 * Format EPSS percentile as ordinal string
 */
export function formatEPSSPercentile(percentile: number): string {
  const pct = Math.round(percentile * 100);
  return `${pct}${getOrdinalSuffix(pct)} percentile`;
}

function getOrdinalSuffix(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

/**
 * Calculate Risk Matrix quadrant based on CVSS and EPSS
 * Quadrants help prioritize remediation:
 * - Q1 (High CVSS, High EPSS): Critical - Immediate action needed
 * - Q2 (High CVSS, Low EPSS): Monitor - Severe but not actively exploited
 * - Q3 (Low CVSS, High EPSS): Urgent - Being exploited despite lower severity
 * - Q4 (Low CVSS, Low EPSS): Scheduled - Lower priority
 */
export type RiskQuadrant = 'Q1' | 'Q2' | 'Q3' | 'Q4';

export interface RiskMatrixResult {
  quadrant: RiskQuadrant;
  label: string;
  description: string;
  priority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  color: string;
  bgColor: string;
}

export function calculateRiskMatrix(cvssScore: number, epssScore: number): RiskMatrixResult {
  const highCVSS = cvssScore >= 7.0;
  const highEPSS = epssScore >= 0.3; // Using 30% as threshold (can be adjusted)

  if (highCVSS && highEPSS) {
    return {
      quadrant: 'Q1',
      label: 'Critical Priority',
      description: 'High severity AND actively being exploited. Requires immediate attention.',
      priority: 'CRITICAL',
      color: 'text-red-700 dark:text-red-300',
      bgColor: 'bg-red-100 dark:bg-red-900/30',
    };
  }

  if (highCVSS && !highEPSS) {
    return {
      quadrant: 'Q2',
      label: 'Monitor Priority',
      description: 'High severity but lower exploitation activity. Plan for remediation.',
      priority: 'HIGH',
      color: 'text-orange-700 dark:text-orange-300',
      bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    };
  }

  if (!highCVSS && highEPSS) {
    return {
      quadrant: 'Q3',
      label: 'Urgent Priority',
      description: 'Lower severity but actively being exploited. Address promptly.',
      priority: 'MEDIUM',
      color: 'text-yellow-700 dark:text-yellow-300',
      bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
    };
  }

  // Q4: Low CVSS, Low EPSS
  return {
    quadrant: 'Q4',
    label: 'Scheduled Priority',
    description: 'Lower severity and exploitation activity. Schedule routine remediation.',
    priority: 'LOW',
    color: 'text-green-700 dark:text-green-300',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
  };
}
